import React, { useState, useEffect } from "react";
import {
  Wind,
  Anchor,
  Zap,
  Heart,
  ChevronDown,
  ChevronRight,
  X,
} from "lucide-react";

const LifeCoachModal = ({ onClose }) => {
  // --- State for Reflections (Auto-Saved) ---
  const [reflections, setReflections] = useState({
    yearReview: "",
    rupture: "",
    repairFri: "",
    repairSat: "",
    repairSun: "",
    repairMon: "",
    support: "",
  });

  // --- Collapsible State for Repair Arc ---
  const [expandedDay, setExpandedDay] = useState("friday"); // 'friday', 'saturday', etc.

  // --- Load from LocalStorage on Mount ---
  useEffect(() => {
    const saved = localStorage.getItem("holiday-os-coach-reflection");
    if (saved) {
      setReflections(JSON.parse(saved));
    }
  }, []);

  // --- Auto-Save on Change ---
  useEffect(() => {
    localStorage.setItem(
      "holiday-os-coach-reflection",
      JSON.stringify(reflections)
    );
  }, [reflections]);

  const updateField = (field, value) => {
    setReflections((prev) => ({ ...prev, [field]: value }));
  };

  const toggleDay = (day) => {
    setExpandedDay(expandedDay === day ? null : day);
  };

  // --- Helper: Repair Day Input ---
  const RepairDay = ({ id, label, placeholder, value, field }) => (
    <div className="border-l-2 border-stone-200 ml-2 pl-4 py-2 transition-all">
      <button
        onClick={() => toggleDay(id)}
        className="flex items-center gap-2 text-sm font-bold text-stone-600 hover:text-[#1F6F54] transition-colors w-full text-left mb-2"
      >
        {expandedDay === id ? (
          <ChevronDown className="w-4 h-4" />
        ) : (
          <ChevronRight className="w-4 h-4" />
        )}
        {label}
      </button>

      {expandedDay === id && (
        <textarea
          value={value}
          onChange={(e) => updateField(field, e.target.value)}
          placeholder={placeholder}
          className="w-full bg-stone-50/50 p-3 rounded text-stone-700 text-sm focus:outline-none focus:bg-[#EAF4EF]/50 transition-colors resize-none leading-relaxed"
          rows={3}
          autoFocus
        />
      )}
      {expandedDay !== id && value && (
        <p className="text-xs text-stone-500 italic line-clamp-1 pl-6">
          {value}
        </p>
      )}
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-stone-900/60 backdrop-blur-sm animate-in fade-in duration-300">
      <div className="bg-white w-full max-w-2xl rounded-xl shadow-2xl overflow-hidden flex flex-col max-h-[95vh]">
        {/* HEADER */}
        <div className="bg-[#1F6F54] p-8 text-white relative shrink-0">
          <button
            onClick={onClose}
            className="absolute top-6 right-6 text-white/60 hover:text-white transition-colors"
          >
            <div className="bg-white/10 rounded-full p-1">
              <X className="w-5 h-5" />
            </div>
          </button>

          <div className="flex items-center gap-2 text-[#EAF4EF] mb-3 text-xs font-bold tracking-widest uppercase opacity-80">
            <Wind className="w-4 h-4" /> 10:00 AM — 11:00 AM
          </div>
          <h2 className="text-3xl font-bold tracking-tight mb-2 font-serif">
            Life Coach Session
          </h2>
          <p className="text-[#EAF4EF] text-base opacity-90 font-light">
            A space to integrate the year — and the repair.
          </p>
        </div>

        {/* SCROLLABLE CONTENT */}
        <div className="overflow-y-auto flex-1 p-8 space-y-10 custom-scrollbar">
          {/* SECTION 1: INTENT */}
          <section className="bg-[#EAF4EF] p-6 rounded-lg border border-[#1F6F54]/10">
            <p className="text-lg font-serif italic text-[#1F6F54] text-center leading-relaxed">
              “Today is not about fixing myself. <br />
              It’s about seeing clearly and speaking honestly.”
            </p>
          </section>

          {/* SECTION 2: YEAR IN REVIEW */}
          <section>
            <div className="flex items-center gap-3 mb-4 text-[#1F6F54]">
              <Anchor className="w-5 h-5" />
              <h3 className="text-sm font-bold uppercase tracking-widest">
                Looking Back on This Year
              </h3>
            </div>
            <div className="space-y-2">
              <label className="block text-sm text-stone-500 font-medium">
                If this coaching session were the only thing I did this week, it
                would still matter because...
              </label>
              <textarea
                value={reflections.yearReview}
                onChange={(e) => updateField("yearReview", e.target.value)}
                placeholder="What I carried, what I survived, what I built..."
                className="w-full bg-stone-50 p-4 rounded-lg text-stone-800 text-base focus:outline-none focus:ring-1 focus:ring-[#1F6F54]/30 min-h-[120px] leading-relaxed"
              />
            </div>
          </section>

          {/* SECTION 3: THE RUPTURE */}
          <section>
            <div className="flex items-center gap-3 mb-4 text-stone-500">
              <Zap className="w-5 h-5" />
              <h3 className="text-sm font-bold uppercase tracking-widest">
                The Thursday Rupture
              </h3>
            </div>
            <div className="relative">
              <textarea
                value={reflections.rupture}
                onChange={(e) => updateField("rupture", e.target.value)}
                placeholder="Leading up to Thursday, the conditions were... The moment things broke down looked like..."
                className="w-full bg-transparent border-l-4 border-stone-200 pl-4 py-2 text-stone-700 text-base focus:outline-none focus:border-stone-400 min-h-[100px] leading-relaxed placeholder:text-stone-300 placeholder:italic"
              />
            </div>
          </section>

          {/* SECTION 4: THE REPAIR ARC */}
          <section>
            <div className="flex items-center gap-3 mb-4 text-rose-800">
              <Heart className="w-5 h-5" />
              <h3 className="text-sm font-bold uppercase tracking-widest">
                How Repair Actually Happened
              </h3>
            </div>
            <div className="space-y-1 bg-white rounded-lg">
              <RepairDay
                id="friday"
                field="repairFri"
                label="Friday"
                value={reflections.repairFri}
                placeholder="What shifted first..."
              />
              <RepairDay
                id="saturday"
                field="repairSat"
                label="Saturday"
                value={reflections.repairSat}
                placeholder="What softened..."
              />
              <RepairDay
                id="sunday"
                field="repairSun"
                label="Sunday"
                value={reflections.repairSun}
                placeholder="What became clearer..."
              />
              <RepairDay
                id="monday"
                field="repairMon"
                label="Monday"
                value={reflections.repairMon}
                placeholder="What stabilized..."
              />
            </div>
          </section>

          {/* SECTION 5: SUPPORT */}
          <section className="pt-6 border-t border-stone-100">
            <h3 className="text-sm font-bold uppercase tracking-widest text-[#1F6F54] mb-4">
              🎯 What I want help with today
            </h3>
            <textarea
              value={reflections.support}
              onChange={(e) => updateField("support", e.target.value)}
              placeholder="If I could use this hour well, I’d want support around..."
              className="w-full bg-[#F5F5F4] p-4 rounded-lg text-stone-800 text-base focus:outline-none focus:ring-1 focus:ring-[#1F6F54]/30 min-h-[80px]"
            />
          </section>
        </div>

        {/* FOOTER */}
        <div className="bg-stone-50 p-4 border-t border-stone-200 flex justify-between items-center text-xs text-stone-400">
          <span className="font-serif italic">
            This is a living record, not a task.
          </span>
          <span>
            {reflections.yearReview || reflections.rupture
              ? "Saved locally"
              : ""}
          </span>
        </div>
      </div>
    </div>
  );
};

export default LifeCoachModal;
